import React, { useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StoreAutocomplete from "../../../../components/Common/StoreAutocomplete";
import type { Store } from "../../../../types/Store";
import type { ManualTransacForm } from "./ui";
import { DialogSection } from "./ui";

interface AddTransactionDialogProps {
    open: boolean;
    onClose: () => void;
    stores: Store[] | undefined;
    manualTransacForm: ManualTransacForm;
    setManualTransacForm: React.Dispatch<React.SetStateAction<ManualTransacForm>>;
    handleSaveManualTransac: (form?: ManualTransacForm, offers?: string[]) => void;
    savingTransac: boolean;
    transacError: string | null;
    offerInput: string;
    setOfferInput: (val: string) => void;
    offerChips: string[];
    setOfferChips: React.Dispatch<React.SetStateAction<string[]>>;
    hasWaitingUpdate?: boolean;
    t: (key: string) => string;
}

// Formulaire tenu en état local (comme AddPointsDialog) : taper dans les champs ne doit pas
// remonter un setState à CustomerDashboard à chaque frappe, ce qui re-rendrait tout le
// dashboard et rendait les inputs perceptiblement lents.
const AddTransactionDialog = ({
    open, onClose, stores, manualTransacForm, setManualTransacForm,
    handleSaveManualTransac, savingTransac, transacError,
    offerInput, setOfferInput, offerChips, setOfferChips,
    hasWaitingUpdate, t,
}: AddTransactionDialogProps) => {
    const [localForm, setLocalForm] = useState<ManualTransacForm>(manualTransacForm);
    const [localOfferInput, setLocalOfferInput] = useState(offerInput);
    const [localOfferChips, setLocalOfferChips] = useState<string[]>(offerChips);

    useEffect(() => {
        if (open) {
            setLocalForm(manualTransacForm);
            setLocalOfferInput(offerInput);
            setLocalOfferChips(offerChips);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const addOfferChip = () => {
        if (!localOfferInput.trim()) return;
        setLocalOfferChips(c => [...c, localOfferInput.trim()]);
        setLocalOfferInput("");
    };

    const handleValidate = () => {
        setManualTransacForm(localForm);
        setOfferInput(localOfferInput);
        setOfferChips(localOfferChips);
        handleSaveManualTransac(localForm, localOfferChips);
    };

    return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <PointOfSaleIcon fontSize="small" color="primary" />
            <Typography fontWeight={700} sx={{ flex: 1 }}>Ajouter une transaction manuelle</Typography>
            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
            {hasWaitingUpdate && (
                <Alert severity="warning" sx={{ mx: 2.5, mt: 2 }}>
                    {t("pages.customers.dashboard.waitingUpdate.transactionBanner")}
                </Alert>
            )}
            {transacError && <Alert severity="error" sx={{ mx: 2.5, mt: 2 }}>{transacError}</Alert>}

            <DialogSection icon={<ReceiptLongIcon />} label="Transaction" />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
                <StoreAutocomplete
                    stores={stores}
                    value={localForm.entityId}
                    onChange={(storeId) => setLocalForm(f => ({ ...f, entityId: storeId }))}
                    label="Magasin"
                    size="small"
                />
                <TextField
                    size="small" label="Date d'achat" type="datetime-local"
                    value={localForm.dateTransaction.replace(" ", "T").slice(0, 16)}
                    onChange={(e) => setLocalForm(f => ({ ...f, dateTransaction: e.target.value.replace("T", " ") + ":00" }))}
                    InputLabelProps={{ shrink: true }} fullWidth
                />
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField size="small" label="Montant" type="number" value={localForm.amount}
                        onChange={(e) => setLocalForm(f => ({ ...f, amount: e.target.value }))} sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ width: 90 }}>
                        <InputLabel>Devise</InputLabel>
                        <Select value={localForm.currencyId} label="Devise"
                            onChange={(e) => setLocalForm(f => ({ ...f, currencyId: e.target.value }))}>
                            <MenuItem value="1">€</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField
                            size="small" label="N° d'offre" value={localOfferInput}
                            onChange={(e) => setLocalOfferInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && localOfferInput.trim()) {
                                    e.preventDefault();
                                    addOfferChip();
                                }
                            }}
                            sx={{ flex: 1 }}
                        />
                        <IconButton
                            color="primary" size="small"
                            onClick={addOfferChip}
                            sx={{ border: "1px solid", borderColor: "primary.main", borderRadius: 1, px: 1 }}
                        >
                            <AddCircleOutlineIcon />
                        </IconButton>
                    </Box>
                    {localOfferChips.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                            {localOfferChips.map((chip, idx) => (
                                <Chip key={idx} label={chip} size="small" onDelete={() => setLocalOfferChips(c => c.filter((_, i) => i !== idx))} />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            <DialogSection icon={<EventNoteIcon />} label="Options" />
            <Box sx={{ px: 2.5, pt: 2, pb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Motif</InputLabel>
                        <Select value={localForm.motif} label="Motif"
                            onChange={(e) => setLocalForm(f => ({ ...f, motif: e.target.value }))}>
                            <MenuItem value="AUTRE">Autre</MenuItem>
                            <MenuItem value="CONFIRM">Confirm</MenuItem>
                            <MenuItem value="DEMANDE SIEGE">Demande siège</MenuItem>
                            <MenuItem value="DOUBLON">Doublon</MenuItem>
                            <MenuItem value="ERREUR MONTANT">Erreur montant</MenuItem>
                            <MenuItem value="ERREUR PTS SUPP">Erreur pts supp</MenuItem>
                            <MenuItem value="OUBLI CARTE">Oubli de carte</MenuItem>
                            <MenuItem value="PARRAIN">Parrain</MenuItem>
                            <MenuItem value="TPE/CAISSE HS">TPE/Caisse HS</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel>Média</InputLabel>
                        <Select value={localForm.media} label="Média"
                            onChange={(e) => setLocalForm(f => ({ ...f, media: e.target.value }))}>
                            <MenuItem value="AUTRE">Autre</MenuItem>
                            <MenuItem value="COURRIER">Courrier</MenuItem>
                            <MenuItem value="EMAIL">Email</MenuItem>
                            <MenuItem value="FAX">Fax</MenuItem>
                            <MenuItem value="QUESTIONNAIRE">Questionnaire</MenuItem>
                            <MenuItem value="TELEPHONE">Téléphone</MenuItem>
                            <MenuItem value="TICKET">Ticket</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField size="small" label="N° Caisse (optionnel)" value={localForm.cashDeskNumber}
                        onChange={(e) => setLocalForm(f => ({ ...f, cashDeskNumber: e.target.value }))} sx={{ flex: 1 }} />
                    <TextField size="small" label="N° Ticket (optionnel)" value={localForm.receiptNumber}
                        onChange={(e) => setLocalForm(f => ({ ...f, receiptNumber: e.target.value }))} sx={{ flex: 1 }} />
                </Box>
            </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
            <Button onClick={onClose} color="inherit" disabled={savingTransac}>Annuler</Button>
            <Button
                variant="contained" onClick={handleValidate}
                disabled={savingTransac || !localForm.amount || !localForm.entityId}
                startIcon={savingTransac ? <CircularProgress size={16} color="inherit" /> : <PointOfSaleIcon />}
            >
                {savingTransac ? "Enregistrement…" : "Valider"}
            </Button>
        </DialogActions>
    </Dialog>
    );
};

export default AddTransactionDialog;
